// src/hooks/useStockData.js
// ─────────────────────────────────────────────────────────────
// Yahoo Finance v8/chart — polls every 2s for live quotes.
// fetchCandles supports intraday: 1m, 5m, 15m, 1h intervals.
//
// Uses two-strategy fetch: proxy first, then direct fallback.

import { useState, useEffect, useCallback, useRef } from "react";
import { authFetch } from "../lib/authFetch";

// ── Proxy URL helper ───────────────────────────────────────
export function proxyUrl(url) {
  return `/api/proxy?url=${encodeURIComponent(url)}`;
}

// ── Two-strategy fetch (proxy → direct fallback) ──────────
async function fetchYahoo(url) {
  // Try proxy first (with JWT so the auth gate passes)
  try {
    const res = await authFetch(proxyUrl(url));
    if (res.ok) return res;
    // On 401/403/429 try direct (no proxy, no auth header needed)
    if (![401, 403, 429].includes(res.status)) return res;
  } catch { /* fall through */ }

  // Direct fallback (CORS-safe browsers only — works in dev)
  return fetch(url, {
    headers: { "User-Agent": "Mozilla/5.0", "Accept": "application/json" },
  });
}

// ── Single quote fetch ─────────────────────────────────────
export async function fetchQuote(ticker) {
  const t   = ticker === "BRK" ? "BRK-B" : ticker;
  const url = `https://query1.finance.yahoo.com/v8/finance/chart/${t}?interval=1d&range=1d&includePrePost=false`;

  const res  = await fetchYahoo(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json   = await res.json();
  const result = json?.chart?.result?.[0];
  if (!result) throw new Error(json?.chart?.error?.description ?? "No data");

  const meta = result.meta ?? {};
  const q    = result.indicators?.quote?.[0] ?? {};
  const adj  = result.indicators?.adjclose?.[0]?.adjclose ?? q.close ?? [];

  const price     = meta.regularMarketPrice ?? adj[adj.length - 1] ?? 0;
  const prev      = meta.chartPreviousClose ?? meta.previousClose  ?? price;
  const change    = price - prev;
  const changePct = prev ? (change / prev) * 100 : 0;

  return {
    ticker,
    name:      meta.longName ?? meta.shortName ?? ticker,
    price:     +price.toFixed(2),
    change:    +change.toFixed(2),
    changePct: +changePct.toFixed(2),
    volume:    meta.regularMarketVolume ?? null,
    marketCap: meta.marketCap           ?? null,
    currency:  meta.currency            ?? "USD",
    high52:    meta.fiftyTwoWeekHigh    ?? null,
    low52:     meta.fiftyTwoWeekLow     ?? null,
    dayHigh:   meta.regularMarketDayHigh ?? null,
    dayLow:    meta.regularMarketDayLow  ?? null,
    open:      meta.regularMarketOpen   ?? null,
    positive:  changePct >= 0,
    updated:   Date.now(),
  };
}

// ── 7-day sparkline ────────────────────────────────────────
export async function fetchSparkline(ticker) {
  const t   = ticker === "BRK" ? "BRK-B" : ticker;
  const url = `https://query1.finance.yahoo.com/v8/finance/chart/${t}?interval=1d&range=7d&includePrePost=false`;

  try {
    const res  = await fetchYahoo(url);
    if (!res.ok) return [];
    const json   = await res.json();
    const result = json?.chart?.result?.[0];
    if (!result) return [];

    const q   = result.indicators?.quote?.[0] ?? {};
    const adj = result.indicators?.adjclose?.[0]?.adjclose ?? q.close ?? [];
    const ts  = result.timestamp ?? [];

    return ts.map((t, i) => ({
      date:   new Date(t * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      v:      adj[i] != null ? +adj[i].toFixed(2) : null,
      open:   q.open?.[i]   != null ? +q.open[i].toFixed(2)  : null,
      high:   q.high?.[i]   != null ? +q.high[i].toFixed(2)  : null,
      low:    q.low?.[i]    != null ? +q.low[i].toFixed(2)   : null,
      close:  adj[i]        != null ? +adj[i].toFixed(2)     : null,
      volume: q.volume?.[i] ?? null,
    })).filter(d => d.v !== null);
  } catch {
    return [];
  }
}

// ── OHLCV candles ──────────────────────────────────────────
export async function fetchCandles(ticker, range = "5d") {
  const t   = ticker === "BRK" ? "BRK-B" : ticker;
  const { interval, yahooRange } = resolveIntervalRange(range);
  const url = `https://query1.finance.yahoo.com/v8/finance/chart/${t}?interval=${interval}&range=${yahooRange}&includePrePost=false`;

  const res  = await fetchYahoo(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json   = await res.json();
  const result = json?.chart?.result?.[0];
  if (!result) throw new Error(json?.chart?.error?.description ?? "No candle data");

  const ts  = result.timestamp ?? [];
  const q   = result.indicators?.quote?.[0] ?? {};
  const adj = result.indicators?.adjclose?.[0]?.adjclose ?? q.close ?? [];

  const isIntraday = ["1m","2m","5m","15m","30m","1h"].includes(interval);

  return ts.map((stamp, i) => {
    const d = new Date(stamp * 1000);
    let date;
    if (isIntraday) {
      const hhmm = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
      date = yahooRange !== "1d"
        ? `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })} ${hhmm}`
        : hhmm;
    } else {
      date = d.toLocaleDateString("en-US", {
        month: "short", day: "numeric",
        ...((yahooRange === "5y" || yahooRange === "2y") ? { year: "2-digit" } : {}),
      });
    }
    return {
      date,
      open:   q.open?.[i]   != null ? +q.open[i].toFixed(2)  : null,
      high:   q.high?.[i]   != null ? +q.high[i].toFixed(2)  : null,
      low:    q.low?.[i]    != null ? +q.low[i].toFixed(2)   : null,
      close:  adj[i]        != null ? +adj[i].toFixed(2)     : null,
      volume: q.volume?.[i] ?? null,
    };
  }).filter(d => d.close !== null);
}

// ── Range → interval resolver ──────────────────────────────
export function resolveIntervalRange(range) {
  const map = {
    "1d-1m":  { interval: "1m",  yahooRange: "1d"  },
    "1d":     { interval: "5m",  yahooRange: "1d"  },
    "5d":     { interval: "5m",  yahooRange: "5d"  },
    "5d-15m": { interval: "15m", yahooRange: "5d"  },
    "1mo-1h": { interval: "1h",  yahooRange: "1mo" },
    "1mo":    { interval: "1d",  yahooRange: "1mo" },
    "3mo":    { interval: "1d",  yahooRange: "3mo" },
    "6mo":    { interval: "1d",  yahooRange: "6mo" },
    "1y":     { interval: "1wk", yahooRange: "1y"  },
    "2y":     { interval: "1wk", yahooRange: "2y"  },
    "5y":     { interval: "1mo", yahooRange: "5y"  },
  };
  return map[range] ?? { interval: "5m", yahooRange: "5d" };
}

// ── Stock news ─────────────────────────────────────────────
export async function fetchStockNews(ticker) {
  const t   = ticker === "BRK" ? "BRK-B" : ticker;
  const url = `https://query1.finance.yahoo.com/v1/finance/search?q=${encodeURIComponent(t)}&quotesCount=0&newsCount=8`;

  let res = await fetchYahoo(url);
  // On 401 the proxy already invalidated its crumb cache — retry once
  if (res.status === 401) {
    await new Promise(r => setTimeout(r, 800));
    res = await fetchYahoo(url);
  }
  if (!res.ok) throw new Error("News fetch failed");
  const json = await res.json();
  return (json?.news ?? []).map(n => ({
    title:  n.title,
    url:    n.link,
    source: n.publisher,
    time:   n.providerPublishTime
      ? new Date(n.providerPublishTime * 1000).toLocaleString()
      : "",
  }));
}

// ── Ticker search ──────────────────────────────────────────
export async function searchTicker(query) {
  const url = `https://query1.finance.yahoo.com/v1/finance/search?q=${encodeURIComponent(query)}&quotesCount=6&newsCount=0`;
  const res  = await fetchYahoo(url);
  if (!res.ok) throw new Error("Search failed");
  const json = await res.json();
  return (json?.quotes ?? [])
    .filter(q => q.quoteType === "EQUITY" || q.quoteType === "ETF")
    .slice(0, 6);
}

// ═══════════════════════════════════════════════════════════
// Global hook — single instance in App.jsx
// ═══════════════════════════════════════════════════════════
const quoteCache     = {};
const sparklineCache = {};

export function useStockData(tickers = [], pollInterval = 2000) {
  const [quotes,     setQuotes]     = useState({});
  const [loading,    setLoading]    = useState(false);
  const [errors,     setErrors]     = useState({});
  const [lastUpdate, setLastUpdate] = useState(null);

  const pollRef   = useRef(null);
  const tickerKey = [...tickers].sort().join(",");

  const fetchOne = useCallback(async (ticker) => {
    const q = await fetchQuote(ticker);
    if (!sparklineCache[ticker]) {
      try { sparklineCache[ticker] = await fetchSparkline(ticker); }
      catch { sparklineCache[ticker] = []; }
    }
    const full = { ...q, sparkline: sparklineCache[ticker] };
    quoteCache[ticker] = full;
    return full;
  }, []);

  const poll = useCallback(async (list, isInitial = false) => {
    if (!list?.length) {
      setLoading(false);
      return;
    }
    if (isInitial) setLoading(true);
    const newQ = {}, newE = {};
    await Promise.allSettled(list.map(async t => {
      try   { newQ[t] = await fetchOne(t); }
      catch (e) { newE[t] = e.message; }
    }));
    setQuotes(prev => ({ ...prev, ...newQ }));
    setErrors(prev => {
      const next = { ...prev, ...newE };
      Object.keys(newQ).forEach(ticker => delete next[ticker]);
      return next;
    });
    setLoading(false); // always clear — fixes freeze when recurring polls stall
    setLastUpdate(new Date());
  }, [fetchOne]);

  useEffect(() => {
    if (!tickers.length) {
      clearInterval(pollRef.current);
      setLoading(false);
      setQuotes({});
      setErrors({});
      setLastUpdate(null);
      return;
    }

    function startPolling() {
      clearInterval(pollRef.current);
      pollRef.current = setInterval(() => poll(tickers), pollInterval);
    }

    function handleVisibility() {
      if (document.visibilityState === "visible") {
        // Tab regained focus — immediately re-fetch and restart interval
        poll(tickers, true);
        startPolling();
      } else {
        // Tab hidden — pause polling to avoid stale promise pile-up
        clearInterval(pollRef.current);
      }
    }

    poll(tickers, true);
    startPolling();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearInterval(pollRef.current);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  // eslint-disable-next-line
  }, [tickerKey]);

  // Safety timeout — if loading is still true after 10s, force-clear it
  useEffect(() => {
    if (!loading) return;
    const timeout = setTimeout(() => setLoading(false), 10000);
    return () => clearTimeout(timeout);
  }, [loading]);

  const getQuote = useCallback(t => quotes[t] ?? quoteCache[t] ?? null, [quotes]);

  return {
    quotes, errors, loading, lastUpdate, getQuote,
    refresh: () => poll(tickers, true),
  };
}
